import moment from "moment";
import { helpers } from "@smartintegrity/questionnaire";
import { FOLLOW_UP_CHILD_TITLE_BY_TYPE } from "./questionnaireDesignerAdapters";

/**
 * Question types the shared helpers resolve through `question.options`.
 */
const TYPES_RESOLVED_THROUGH_OPTIONS = [
  "radiobox",
  "select",
  "country_list",
  "checkbox",
  "category",
];

const hasMeaningfulSections = (questions) => {
  return questions.some((question) => {
    return (
      question?.section !== undefined &&
      question?.section !== null &&
      String(question.section).trim() !== ""
    );
  });
};

/**
 * Used only for legacy follow-ups that were stored on `option.type` instead of
 * as children. New sub-questions persist their own `title`.
 */
const childTitleForType = (type) => {
  return (
    FOLLOW_UP_CHILD_TITLE_BY_TYPE[type] ||
    `Enter ${String(type || "value")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase())}`
  );
};

/**
 * Legacy GDPR radiobox follow-ups: `option.type` is `date`/`text`/… and
 * `children` is empty. The runtime mapper used to invent a child titled from
 * `extended_title` or the field type.
 */
const isLegacyFollowUpOption = (option, questionType) => {
  const hasChildren =
    Array.isArray(option?.children) && option.children.length > 0;

  if (hasChildren) {
    return false;
  }

  const optionType = option?.type;

  return !!(
    optionType &&
    optionType !== questionType &&
    optionType !== "radiobox"
  );
};

const mapDefinitionChild = (child) => {
  return {
    ...child,
    id: child.id,
    type: child.type,
    title: child.title || child.extended_title || "",
  };
};

/**
 * Prefer the definition catalog when the backend sends `options`.
 * New options already carry `children` with a stored `title`.
 * Old options keep the follow-up on `type`/`extended_title`.
 */
const mapDefinitionOptions = (options, questionType, answers) => {
  return options.map((option) => {
    if (Array.isArray(option.children) && option.children.length > 0) {
      return {
        ...option,
        children: option.children.map(mapDefinitionChild),
      };
    }

    if (isLegacyFollowUpOption(option, questionType)) {
      const selectedAnswer = answers.find((answer) => {
        return parseInt(answer.value, 10) === parseInt(option.id, 10);
      });
      const matchedChild = (selectedAnswer?.children || []).find((child) => {
        return child.type === option.type;
      });

      return {
        ...option,
        type: questionType,
        children: [
          {
            id: matchedChild?.id ?? `${option.id}-1`,
            type: option.type,
            title:
              option.extended_title ||
              matchedChild?.extended_title ||
              childTitleForType(option.type),
          },
        ],
      };
    }

    return {
      ...option,
      children: option.children || [],
    };
  });
};

const SYNTHETIC_MULTI_VALUE_CHILD_SUFFIX = "-multi-select";

/**
 * Sub-question saved before the library tagged its selections with an owner.
 * Every untagged `{ value, title }` entry of the option belongs to it.
 */
const syntheticMultiValueChildId = (optionId) => {
  return `${optionId}${SYNTHETIC_MULTI_VALUE_CHILD_SUFFIX}`;
};

/**
 * Rebuild a catalog from saved answers when `options` is absent.
 *
 * A child answer's `title` is the entered value, not the sub-question label.
 * New children therefore get an empty label here (the definition title lives
 * on `options[].children[].title` when the backend sends it). Legacy children
 * — `extended_title`, or the old `${optionId}-1` id — still use a type-based
 * title so old reports keep "Select Date" / "Enter Text".
 *
 * Multi value sub-questions are saved as one untagged `{ value, title }` entry
 * per selection, so they collapse back into a single child.
 */
const synthesizeOptionsFromAnswers = (answers) => {
  return answers
    .filter((answer) => {
      return answer?.value !== undefined && answer?.value !== null;
    })
    .map((answer) => {
      const childAnswers = answer?.children || [];

      const seenChildIds = new Set();

      const children = childAnswers
        .filter((child) => {
          if (child?.id === undefined || child?.id === null) {
            return false;
          }

          // A multi value sub-question saves one entry per selection.
          if (seenChildIds.has(String(child.id))) {
            return false;
          }

          seenChildIds.add(String(child.id));

          return true;
        })
        .map((child) => {
          const isLegacyChild =
            !!child.extended_title ||
            String(child.id) === `${answer.value}-1`;

          return {
            id: child.id,
            type: child.type,
            title: isLegacyChild
              ? child.extended_title || childTitleForType(child.type)
              : "",
          };
        });

      const hasUntaggedChildren = childAnswers.some((child) => {
        return child?.id === undefined || child?.id === null;
      });

      if (hasUntaggedChildren) {
        children.push({
          id: syntheticMultiValueChildId(answer.value),
          type: "multi_select",
          title: "",
        });
      }

      return {
        id: answer.value,
        title: answer.title,
        children,
      };
    });
};

const normalizeOptions = (record, answers) => {
  if (!TYPES_RESOLVED_THROUGH_OPTIONS.includes(record?.type)) {
    return record?.options;
  }

  if (Array.isArray(record?.options) && record.options.length > 0) {
    return mapDefinitionOptions(record.options, record.type, answers);
  }

  return synthesizeOptionsFromAnswers(answers);
};

/**
 * Prefer `answers` (the `question_answers` contract). Fall back to `answer`
 * so older payloads still map.
 */
const normalizeRecords = (records) => {
  return records.map((record) => {
    const answers = record?.answers || record?.answer || [];

    return {
      ...record,
      answers,
      options: normalizeOptions(record, answers),
    };
  });
};

/**
 * Build the shared AnswerSheet inputs from GDPR `report.question_answers`.
 *
 * Same pipeline as the TPDD consumer: the records already carry both the
 * question definition and its saved values, so the same array is handed to both
 * questionnaire helpers. `multiTab` follows the data, since GDPR questionnaires
 * are usually one flat list without `section`/`sub_section`.
 */
export const gdprQuestionnaireDetailToAnswerSheet = (questionAnswers) => {
  if (!Array.isArray(questionAnswers) || questionAnswers.length === 0) {
    return {
      questions: [],
      answers: [],
      multiTab: false,
    };
  }

  const records = normalizeRecords(questionAnswers);
  const multiTab = hasMeaningfulSections(records);

  return {
    questions: helpers.makeQuestionsReadyForAnswerSheet(records, multiTab),
    answers: helpers.makeAnswersReadyForAnswerSheet(records, records),
    multiTab,
  };
};

const MULTI_VALUE_SELECT_TYPES = [
  "multi_select",
  "country_multi_select",
  "legal_entity",
];

const SINGLE_VALUE_SELECT_TYPES = ["select", "country_list"];

/**
 * Types whose answers are a list of selections rather than a single value.
 */
export const MULTI_VALUE_EXPORT_TYPES = [
  "checkbox",
  "category",
  ...MULTI_VALUE_SELECT_TYPES,
];

const isSelectLikeChild = (child) => {
  return (
    MULTI_VALUE_SELECT_TYPES.includes(child?.type) ||
    SINGLE_VALUE_SELECT_TYPES.includes(child?.type)
  );
};

/**
 * A nested radiobox without a catalog falls back to the same labels the
 * AnswerSheet uses.
 */
const DEFAULT_RADIOBOX_OPTIONS = [
  { id: 1, title: "Yes" },
  { id: 0, title: "No" },
];

/**
 * A selection reaches us as a `{ value, title }` entry, as a sub-question that
 * owns its selections under `children`, or as a plain option id.
 */
const answerLabels = (answer, catalog = []) => {
  if (answer === null || answer === undefined) {
    return [];
  }

  if (Array.isArray(answer)) {
    return answer.reduce((labels, item) => {
      return labels.concat(answerLabels(item, catalog));
    }, []);
  }

  if (typeof answer === "object") {
    const nestedValues = answer.children ?? answer.answer ?? answer.values;

    if (Array.isArray(nestedValues) && nestedValues.length > 0) {
      return answerLabels(nestedValues, catalog);
    }

    const label = answer.label ?? answer.title;

    return label === null || label === undefined || label === ""
      ? []
      : [String(label)];
  }

  // Older answers stored the picked option id only.
  const catalogOption = catalog.find((option) => {
    return String(option?.id) === String(answer);
  });

  return [String(catalogOption?.title ?? answer)];
};

const exportValues = (type, answers, catalog = []) => {
  switch (type) {
    case "date":
      return answers
        .map((answer) => {
          return answer?.title
            ? moment(answer.title).format("DD/MM/YYYY")
            : "";
        })
        .filter(Boolean);

    // The AnswerSheet shows the uploaded file name, never the stored
    // attachment id, and shows nothing when the name was not saved.
    case "file_upload":
      return answers
        .map((answer) => {
          return answer?.name || "";
        })
        .filter(Boolean);

    default:
      return answerLabels(answers, catalog);
  }
};

const isTaggedChildAnswer = (answer) => {
  return answer?.id !== undefined && answer?.id !== null;
};

const childOwnsAnswerValue = (child, answer) => {
  const options = Array.isArray(child?.options) ? child.options : [];

  if (options.length < 1) {
    return false;
  }

  return options.some((option) => {
    return String(option?.id) === String(answer?.value);
  });
};

/**
 * Maps every sub-question of an option to its own saved answers, mirroring the
 * shared AnswerSheet.
 *
 * New sub-questions tag their answers with their own id. Older selections are
 * untagged `{ value, title }` entries, so ownership is inferred: first through
 * each sub-question's own option catalog, then by position, since the entries
 * follow the order the sub-questions were defined in. A single select owns one
 * entry and a lone multi value sub-question owns the rest. Two catalog-less
 * multi value sub-questions under the same option cannot be told apart and are
 * left empty rather than shown with each other's selections.
 */
const resolveOptionChildrenAnswers = (children = [], childrenAnswers = []) => {
  const resolved = new Map();

  children.forEach((child) => {
    resolved.set(
      child.id,
      childrenAnswers.filter((answer) => {
        return (
          isTaggedChildAnswer(answer) &&
          String(answer.id) === String(child.id)
        );
      })
    );
  });

  let unclaimedAnswers = childrenAnswers.filter((answer) => {
    return !isTaggedChildAnswer(answer);
  });

  if (unclaimedAnswers.length < 1) {
    return resolved;
  }

  const claim = (child, claimedAnswers) => {
    if (claimedAnswers.length < 1) {
      return;
    }

    resolved.set(child.id, claimedAnswers);

    unclaimedAnswers = unclaimedAnswers.filter((answer) => {
      return !claimedAnswers.includes(answer);
    });
  };

  const isWaiting = (child) => {
    return (resolved.get(child.id) || []).length < 1;
  };

  const selectLikeChildren = children.filter((child) => {
    return isSelectLikeChild(child) && isWaiting(child);
  });

  selectLikeChildren.forEach((child) => {
    claim(
      child,
      unclaimedAnswers.filter((answer) => {
        return childOwnsAnswerValue(child, answer);
      })
    );
  });

  const orderedWaitingChildren = selectLikeChildren.filter(isWaiting);

  let ownerIsAmbiguous = false;

  orderedWaitingChildren.forEach((child, index) => {
    if (ownerIsAmbiguous || unclaimedAnswers.length < 1) {
      return;
    }

    if (SINGLE_VALUE_SELECT_TYPES.includes(child.type)) {
      claim(child, unclaimedAnswers.slice(0, 1));

      return;
    }

    const followingChildren = orderedWaitingChildren.slice(index + 1);

    const followingMultiValueChildren = followingChildren.filter(
      (followingChild) => {
        return MULTI_VALUE_SELECT_TYPES.includes(followingChild.type);
      }
    );

    if (followingMultiValueChildren.length > 0) {
      ownerIsAmbiguous = true;

      return;
    }

    claim(
      child,
      unclaimedAnswers.slice(
        0,
        unclaimedAnswers.length - followingChildren.length
      )
    );
  });

  return resolved;
};

const toExportChild = (child, childAnswers) => {
  // A single value sub-question shows its first answer only, like the
  // AnswerSheet does.
  const answers = MULTI_VALUE_SELECT_TYPES.includes(child.type)
    ? childAnswers
    : childAnswers.slice(0, 1);

  const item = {
    id: child.id,
    title: child.title || "",
    type: child.type,
    values: [],
    children: [],
  };

  if (child.type === "radiobox") {
    const options = child.options || DEFAULT_RADIOBOX_OPTIONS;
    const selectedOption = options.find((option) => {
      return String(option?.id) === String(answers[0]?.value);
    });

    item.values = selectedOption?.title ? [selectedOption.title] : [];

    return item;
  }

  item.values = exportValues(child.type, answers, child.options || []);

  return item;
};

const toExportItem = (record) => {
  const answers = record.answers || [];
  const options = record.options || [];

  const item = {
    id: record.id,
    title: record.title || "",
    type: record.type,
    values: [],
    children: [],
  };

  if (record.type === "radiobox") {
    const selectedAnswer = answers[0];
    const selectedOption = options.find((option) => {
      return String(option?.id) === String(selectedAnswer?.value);
    });

    item.values = answerLabels(
      selectedAnswer?.title ?? selectedAnswer?.value,
      options
    );

    const children = selectedOption?.children || [];

    const answersByChild = resolveOptionChildrenAnswers(
      children,
      selectedAnswer?.children || []
    );

    item.children = children.map((child) => {
      return toExportChild(child, answersByChild.get(child.id) || []);
    });

    return item;
  }

  item.values = exportValues(record.type, answers, options);

  return item;
};

/**
 * Questionnaire records carry the answers, while the questionnaire definition
 * (`report.contents` for the GDPR exports) is the only place older reports keep
 * the question type.
 */
const withDefinition = (record, definitionsById) => {
  const definition = definitionsById.get(String(record?.id)) || {};

  return {
    ...record,
    title: record?.title || definition.title || "",
    type: record?.type || definition.type,
    options: record?.options || definition.options,
    answers: record?.answers || record?.answer || definition.answer || [],
  };
};

/**
 * Build a renderer agnostic model of a filled questionnaire for the exports
 * (PDF today). Answers run through the same normalization as the AnswerSheet,
 * so an exported report shows the same values as the detail page: nested
 * sub-questions keep their own selections and multi value questions keep every
 * selected option.
 */
export const gdprQuestionnaireDetailToExportModel = (
  questionAnswers,
  definitions = []
) => {
  if (!Array.isArray(questionAnswers) || questionAnswers.length === 0) {
    return [];
  }

  const definitionsById = new Map(
    (Array.isArray(definitions) ? definitions : []).map((definition) => {
      return [String(definition?.id), definition];
    })
  );

  const records = normalizeRecords(
    questionAnswers.map((record) => {
      return withDefinition(record, definitionsById);
    })
  );

  return records.map(toExportItem);
};
